import { useMsal } from '@azure/msal-react';
import * as Sentry from '@sentry/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { AuthFatalError, getAccessToken, resetAuthInteractionCount } from '@/auth/tokenBroker';
import { get } from '@/helpers/axiosHelper';
import { useGlobalSettingsStore } from '@/store/useGlobalSettingsStore';
import { InteractionStatus } from '@azure/msal-browser';

const MAX_RETRIES = 3;

const useAuth = (): [boolean, boolean, InteractionStatus, AuthFatalError | null] => {
  const { accounts, instance, inProgress } = useMsal();
  const accountId = useMemo(() => accounts[0]?.homeAccountId ?? null, [accounts]);

  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authFatalError, setAuthFatalError] = useState<AuthFatalError | null>(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<number | null>(null);

  const { userEmail, setIsAuthenticated: setGlobalIsAuthenticated, setIsAuthChecking } =
    useGlobalSettingsStore(
      useShallow((state) => ({
        userEmail: state?.globalSettings?.userEmail,
        setIsAuthenticated: state.setIsAuthenticated,
        setIsAuthChecking: state.setIsAuthChecking,
      })),
    );

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const clearRetryTimeout = () => {
      if (retryTimeoutRef.current !== null) {
        window.clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };

    const setAuthState = (authenticated: boolean) => {
      if (!isMounted) return;
      setIsAuthenticated(authenticated);
      setGlobalIsAuthenticated(authenticated);
    };

    const completeAuthCheck = () => {
      if (!isMounted) return;
      setIsLoading(false);
      setIsAuthChecking(false);
    };

    setIsLoading(true);
    setIsAuthChecking(true);
    retryCountRef.current = 0;
    setAuthFatalError(null);

    const checkAuth = async () => {
      try {
        if (accountId) {
          await getAccessToken();
          setAuthState(true);
          completeAuthCheck();
          return;
        } else {
          const { data: response } = await get<boolean>('Login/IsLoggedIn', {
            signal: abortController.signal,
          });
          setAuthState(response);
          completeAuthCheck();
          return;
        }
      } catch (error) {
        if (!isMounted) return;
        if (error instanceof AuthFatalError) {
          try {
            await instance.clearCache();
          } catch {
            // no-op
          }

          resetAuthInteractionCount();
          setAuthState(false);
          setAuthFatalError(error);
          completeAuthCheck();
          return;
        }

        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current += 1;
          clearRetryTimeout();
          retryTimeoutRef.current = window.setTimeout(() => {
            void checkAuth();
          }, 1000);
          return;
        }

        setAuthState(false);
        completeAuthCheck();
      }
    };

    void checkAuth();

    return () => {
      isMounted = false;
      abortController.abort();
      clearRetryTimeout();
      setIsAuthChecking(false);
    };
  }, [accountId, instance, setGlobalIsAuthenticated, setIsAuthChecking]);

  useEffect(() => {
    if (userEmail) {
      Sentry.setUser({ id: userEmail, email: userEmail });
    }
  }, [userEmail]);

  return [isAuthenticated, isLoading, inProgress, authFatalError];
};

export default useAuth;
